import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Quote, Download } from "lucide-react";
import { about, profile, testimonials } from "@/data/content";
import { Reveal, SectionLabel, EASE } from "./Reveal";

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="about" ref={ref} className="relative bg-white py-24 md:py-32" data-testid="about-section">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }} className="lg:col-span-5">
          <motion.div variants={{ hidden: { clipPath: "inset(100% 0% 0% 0% round 28px)" }, show: { clipPath: "inset(0% 0% 0% 0% round 28px)", transition: { duration: 1.3, ease: EASE } } }}
            className="relative aspect-[4/5] overflow-hidden rounded-[28px] bg-[#F0F5FA]" data-testid="about-portrait">
            <motion.img style={{ y: imgY, scale: 1.18 }} src="/images/portrait.jpg" alt={`${profile.name} portrait placeholder`} className="h-full w-full object-cover" />
          </motion.div>
        </motion.div>
        <div className="lg:col-span-7">
          <Reveal>
            <SectionLabel index="05">About</SectionLabel>
            <h2 className="text-4xl font-bold leading-[1.05] tracking-[-0.03em] text-[#05325F] sm:text-5xl">Marco Escalante</h2>
            <p className="mt-6 text-base leading-relaxed text-[#4A607A] md:text-lg">{about.bio}</p>
          </Reveal>
          <Reveal delay={0.1} className="mt-10 grid gap-8 sm:grid-cols-2">
            <div data-testid="client-experience">
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[#09549F]">Client experience</p>
              <ul className="space-y-2">{about.clients.map((c, i) => <li key={`${c}-${i}`} className="border-b border-[#E0E8F2] pb-2 text-[#05325F]">{c}</li>)}</ul>
            </div>
            <div>
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[#09549F]">Toolkit</p>
              <div className="flex flex-wrap gap-2">{about.tools.map((t) => <span key={t} className="rounded-full bg-[#F0F5FA] px-3 py-1.5 text-sm font-bold text-[#05325F]">{t}</span>)}</div>
              <a href={profile.resumeUrl} data-testid="about-resume-button"
                className="group mt-8 inline-flex items-center gap-2 rounded-full border-2 border-[#05325F] px-5 py-3 font-bold text-[#05325F] transition-colors hover:bg-[#05325F] hover:text-white">
                <Download className="h-4 w-4 transition-transform group-hover:translate-y-0.5" /> Download résumé
              </a>
            </div>
          </Reveal>
        </div>
        <div className="grid gap-5 md:grid-cols-2 lg:col-span-12">
          {testimonials.map((t, i) => (
            <Reveal key={i} delay={i * 0.1} className="rounded-[24px] bg-[#F0F5FA] p-6 md:p-8" data-testid={`testimonial-${i}`}>
              <Quote className="h-8 w-8 text-[#005EB8]" />
              <p className="mt-4 text-lg leading-relaxed text-[#05325F]">{t.quote}</p>
              <p className="mt-6 font-bold text-[#05325F]">{t.name}</p>
              <p className="text-sm text-[#4A607A]">{t.title}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
